import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { FluentProvider, webDarkTheme, webLightTheme } from "@fluentui/react-components";
import { ChevronLeftRegular, ChevronRightRegular } from "@fluentui/react-icons";
import styles from "./Pagination.module.scss";

interface IPaginationProps {
  ariaLabel: string;
  currentPage: number;
  totalPages: number;
  previousPageLabel: string;
  nextPageLabel: string;
  pageIndicator: string;
  onPageChange: (page: number) => void;
}

export function Pagination({
  ariaLabel,
  currentPage,
  totalPages,
  previousPageLabel,
  nextPageLabel,
  pageIndicator,
  onPageChange,
}: IPaginationProps): ReactNode {
  const [prefersDarkMode, setPrefersDarkMode] = useState(
    typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleThemeChange = (event: MediaQueryListEvent): void => {
      setPrefersDarkMode(event.matches);
    };

    mediaQuery.addEventListener("change", handleThemeChange);

    return (): void => {
      mediaQuery.removeEventListener("change", handleThemeChange);
    };
  }, []);

  if (totalPages <= 1) {
    return null;
  }

  return (
    <FluentProvider
      className={styles.provider}
      theme={prefersDarkMode ? webDarkTheme : webLightTheme}
    >
      <nav className={styles.pagination} aria-label={ariaLabel}>
        <button
          type="button"
          className={styles.navigationButton}
          onClick={(): void => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
        >
          <ChevronLeftRegular className={styles.navigationIcon} aria-hidden="true" />
          {previousPageLabel}
        </button>

        <div className={styles.pageNumbers}>
          {Array.from({ length: totalPages }, (_, index) => {
            const page = index + 1;
            const isCurrentPage = page === currentPage;

            return (
              <button
                key={page}
                type="button"
                className={`${styles.pageButton} ${isCurrentPage ? styles.currentPage : ""}`}
                aria-current={isCurrentPage ? "page" : undefined}
                onClick={(): void => onPageChange(page)}
              >
                {page}
              </button>
            );
          })}
        </div>

        <span className={styles.livePageIndicator} aria-live="polite">
          {pageIndicator}
        </span>

        <button
          type="button"
          className={styles.navigationButton}
          onClick={(): void => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
        >
          {nextPageLabel}
          <ChevronRightRegular className={styles.navigationIcon} aria-hidden="true" />
        </button>
      </nav>
    </FluentProvider>
  );
}
